import type { ReactNode } from 'react'
import { Anchor, Stack, Text } from '@mantine/core'
import { IconExternalLink } from '@tabler/icons-react'
import { MetaList, SectionCard } from '@lighthouse/design'
import { NOT_MEASURED, exactNumber } from '../../utils/format'
import { hangfireJobUrl } from '../../utils/hangfireUrl'
import type { EtlRunDetail } from '../../types/etlRuns'

/**
 * The ETL Explorer's Logs tab: the Hangfire job that CREATED this run, and
 * plainly not a log.
 *
 * Observatory has no log store and nothing writes log lines to Hangfire
 * (Hangfire.Console is not installed), so the most this tab can offer is the
 * job's own page: its state history and, if it failed, the exception. The id is
 * the parent run's `HangfireJobId`, written once when the run is created
 * (EtlRunTelemetry) and kept by a resume — so it names the job that created the
 * run, never necessarily the one that did this entity's work. The copy says so
 * every time, because a link labelled "logs" that opens another job's page is
 * the kind of confident answer this surface was rebuilt to stop giving.
 *
 * The link follows utils/hangfireUrl's contract: a job page only when the build
 * knows the dashboard's address, the id as plain text when it does not — never
 * a relative `/hangfire` href into this SPA's own origin.
 */

const NO_ADDRESS =
  'The Hangfire dashboard address is not configured for this build, so the id is shown as text.'

const NO_JOB =
  'No Hangfire job was recorded when this run was created. Runs created before Automaton began recording job ids '
  + 'have none, and so does a run started outside Hangfire.'

const WHAT_THE_PAGE_SHOWS =
  'Hangfire\'s page shows the job\'s state history (enqueued, processing, succeeded or failed) and the exception if '
  + 'it failed. It is not a log: nothing writes log lines to Hangfire, and Observatory has no log store.'

const EXPIRY =
  'Hangfire keeps a succeeded job\'s page for about a day (its default, which Automaton does not change); a failed '
  + 'job\'s page stays until someone deletes it. An older link can open \'expired or not found\'.'

/** A value with a dimmed line of explanation under it. */
function Explained({ value, note }: { value: ReactNode; note: ReactNode }) {
  return (
    <Stack gap={2}>
      <span>{value}</span>
      <Text size="xs" c="dimmed">{note}</Text>
    </Stack>
  )
}

/** The creating job: a link when both an id and the dashboard's address are known, else the id or a dash, with why. */
function CreatingJob({ id }: { id: string | null }) {
  if (id === null) return <Explained value={NOT_MEASURED} note={NO_JOB} />
  const url = hangfireJobUrl(id)
  if (url === null) {
    return <Explained value={<Text span ff="monospace" size="sm">{id}</Text>} note={NO_ADDRESS} />
  }
  return (
    <Anchor href={url} target="_blank" rel="noopener noreferrer" size="sm" ff="monospace">
      {id} <IconExternalLink size={12} style={{ verticalAlign: 'middle' }} />
    </Anchor>
  )
}

export function RunHangfirePanel({ run }: { run: EtlRunDetail }) {
  // A blank id is no id: it would otherwise link to the dashboard's job list.
  const id = run.hangfireJobId?.trim() || null

  return (
    <SectionCard title="Hangfire job" subtitle="Not a log.">
      <Stack gap="sm">
        <MetaList items={[{ label: 'Created this run', value: <CreatingJob id={id} /> }]} />
        <Text size="sm">
          This is the Hangfire job that created run {run.runKey}. A resumed run keeps this id, so work a later request
          did inside this run ran under a different job, which is not recorded.
        </Text>
        {run.attemptCount > 1 && (
          <Text size="sm">
            {run.entityName} was attempted {exactNumber(run.attemptCount)} times in this run. Only the creating job&apos;s
            id is recorded, so attempts after the first may have run under another job.
          </Text>
        )}
        <Text size="sm">{WHAT_THE_PAGE_SHOWS}</Text>
        <Text size="sm" c="dimmed">{EXPIRY}</Text>
      </Stack>
    </SectionCard>
  )
}
